/**
 * Label handling for rendered posts.
 *
 * A post carries labels from two places: self-labels the author attached to
 * their own record, and labels applied by a labeler the AppView resolved for
 * us. Both arrive in the same `labels` array on the post view, and the account
 * carries its own set in `author.labels`.
 *
 * We sort them into three outcomes:
 *   "hide"  the post does not render at all
 *   "blur"  the post renders, media sits behind a click-through
 *   "none"  nothing to do
 *
 * Only the embed acts on "hide". Inside the app a feed owner is authenticated
 * and is looking at their own feed to debug it, so they keep seeing everything.
 */

interface LabelLike {
  val: string;
  /** A negation retracts an earlier label rather than applying one. */
  neg?: boolean;
}

interface LabelledPost {
  labels?: readonly LabelLike[] | null;
  author?: { labels?: readonly LabelLike[] | null } | null;
}

/**
 * Values that take content out of an embed entirely.
 *
 * `!no-unauthenticated` is here because an embed is always an unauthenticated
 * view — that label means "do not show this to logged-out readers", and an
 * iframe on someone else's site is exactly that.
 */
const HIDE_LABEL_VALUES: ReadonlySet<string> = new Set([
  "!hide",
  "!takedown",
  "!no-unauthenticated",
]);

/**
 * Values that leave the post in place but put its media behind a click-through.
 * Covers the self-labels a poster can set, the adult/graphic values Bluesky's
 * moderation service applies, and `!warn` as the generic catch-all.
 */
const BLUR_LABEL_VALUES: ReadonlySet<string> = new Set([
  "!warn",
  "porn",
  "sexual",
  "sexual-figurative",
  "nudity",
  "nsfw",
  "graphic-media",
  "gore",
  "corpse",
  "self-harm",
  "torture",
]);

function applied(labels: readonly LabelLike[] | null | undefined): string[] {
  return (labels ?? []).filter((label) => !label.neg).map((label) => label.val);
}

/** Every label in force on the post itself and on the account that wrote it. */
function allLabels(post: LabelledPost): string[] {
  return [...applied(post.labels), ...applied(post.author?.labels)];
}

/** The post must not render at all in an unauthenticated context. */
export function isHiddenByLabel(post: LabelledPost): boolean {
  return allLabels(post).some((val) => HIDE_LABEL_VALUES.has(val));
}

/** The post renders, but its media starts behind a click-through. */
export function isSensitiveByLabel(post: LabelledPost): boolean {
  return allLabels(post).some((val) => BLUR_LABEL_VALUES.has(val));
}
